/**
 * Reconciles the desktop QR panel's image list against the upload session on the server.
 *
 * The phone owns the photos: it uploads them and it deletes them, and every change goes through
 * the session the desktop polls. So the server's list for that token is the truth and the desktop
 * is a mirror of it — not a list that only ever grows. The poll used to emit only on a count
 * INCREASE, so a delete on the phone (count decreases) reached no branch and the desktop kept, and
 * then submitted, S3 keys that had been removed (frontend#397).
 */

/** Only the fields the upload session owns; anything else is page-local. The pages keep their
 * richer `UploadedImage` types, which satisfy this structurally. */
export interface ServerImageLike {
  s3_key: string;
  extraction_status?: string;
  extracted_products?: unknown[];
  rejection_reason?: string | null;
}

/** Fields the desktop holds that the server never sees, so a resync must carry them over. */
const LOCAL_ONLY_FIELDS = ['localPreview', 'text_input'] as const;

/** The one status meaning "the server has never heard of this image yet" — the client-side
 * `uploading-<ts>` placeholder. `pending` is NOT one (it is the server's own default), so anything
 * else missing from the server's list is a real delete (frontend#397). */
const IN_FLIGHT_ONLY_STATUSES = new Set(['uploading']);

/** The AI's verdict on an image — the fields a final local verdict keeps over a non-final snapshot. */
const VERDICT_FIELDS = ['extraction_status', 'extracted_products', 'rejection_reason'] as const;

const pickFields = <T extends ServerImageLike>(img: T, fields: readonly string[]): Partial<T> => {
  const preserved: Partial<T> = {};
  for (const field of fields) {
    const value = (img as Record<string, unknown>)[field];
    if (value !== undefined) (preserved as Record<string, unknown>)[field] = value;
  }
  return preserved;
};

const pickLocalOnly = <T extends ServerImageLike>(img: T): Partial<T> =>
  pickFields(img, LOCAL_ONLY_FIELDS);

/** A resolved end state: at least one product, or `failed` — with or without a rejection reason,
 * since the reason is nullable. `processing`, `pending`, `uploading` and `completed` with zero
 * products are all still in flight, and every badge or "done" gate must treat them as such. */
export const isImageFinal = (img: ServerImageLike): boolean =>
  (img.extraction_status === 'completed' && (img.extracted_products?.length ?? 0) > 0) ||
  img.extraction_status === 'failed';

/** A pass-1 rejection lives only in the session record; `/status` rebuilds its list from S3 and
 * cannot see it. So a final local verdict is never downgraded by a non-final server snapshot. */
const keepFinalVerdict = <T extends ServerImageLike>(server: T, local: T): Partial<T> =>
  isImageFinal(local) && !isImageFinal(server) ? pickFields(local, VERDICT_FIELDS) : {};

const sameImage = (server: ServerImageLike, local: ServerImageLike | undefined): boolean =>
  !!local &&
  server.s3_key === local.s3_key &&
  server.extraction_status === local.extraction_status &&
  (server.extracted_products?.length ?? 0) === (local.extracted_products?.length ?? 0) &&
  (server.rejection_reason ?? null) === (local.rejection_reason ?? null);

/**
 * True when two image lists differ — by membership, by order, or by any per-image field the server
 * owns. Order counts: the panel renders in list order. Callers compare the MERGED list, so `true`
 * means a real change; emitting only then keeps a 2 s tick from thrashing SurveySession state.
 */
export const hasSessionImagesDiverged = (
  serverImages: readonly ServerImageLike[],
  localImages: readonly ServerImageLike[],
): boolean =>
  serverImages.length !== localImages.length ||
  serverImages.some((server, i) => !sameImage(server, localImages[i]));

/**
 * The server's list, with page-local fields carried across for images it still holds. Without
 * this, adopting the server list verbatim would erase the per-image `text_input` the respondent
 * typed when extraction failed, and drop the local preview so the tile flashes.
 *
 * `keepInFlight` (the phone page only) keeps a local image AT ITS OWN INDEX while it is still
 * `uploading`: the placeholder exists before the upload request reaches the server, so its absence
 * is not a delete, and appending it would reorder the tiles under the respondent's finger.
 * `keepKeys` is the phone's other exception — a photo it uploaded itself, which a listing can lack
 * for a moment while a rejected upload is still being put to S3. Anything else missing from the
 * server's list is a real delete (frontend#397), the desktop mirror included.
 */
export interface MergeOptions {
  keepInFlight?: boolean;
  /** Keys this page uploaded itself — kept at their index even when a listing lacks them. */
  keepKeys?: ReadonlySet<string>;
}

const isInFlight = (img: ServerImageLike): boolean =>
  IN_FLIGHT_ONLY_STATUSES.has(img.extraction_status ?? '');

export const mergeSessionImages = <T extends ServerImageLike>(
  serverImages: readonly T[],
  localImages: readonly T[],
  { keepInFlight = false, keepKeys }: MergeOptions = {},
): T[] => {
  const merged = serverImages.map((server) => {
    const local = localImages.find((img) => img.s3_key === server.s3_key);
    return local
      ? { ...server, ...pickLocalOnly(local), ...keepFinalVerdict(server, local) }
      : server;
  });
  if (!keepInFlight && !keepKeys) return merged;
  const serverKeys = new Set(serverImages.map((img) => img.s3_key));
  localImages.forEach((img, index) => {
    if (serverKeys.has(img.s3_key)) return;
    if (!((keepInFlight && isInFlight(img)) || keepKeys?.has(img.s3_key))) return;
    merged.splice(Math.min(index, merged.length), 0, img);
  });
  return merged;
};

/** Swaps an upload placeholder for the server's record without ever holding one s3_key twice: a
 * status poll may already have adopted the server copy, since the server lists an image before the
 * upload response reaches the phone. Then the placeholder goes and that copy keeps the preview. */
export const adoptUploadedImage = <T extends ServerImageLike>(
  images: readonly T[],
  placeholderKey: string,
  uploaded: T,
): T[] => {
  if (!images.some((img) => img.s3_key === uploaded.s3_key)) {
    return images.map((img) => (img.s3_key === placeholderKey ? uploaded : img));
  }
  const localOnly = pickLocalOnly(uploaded);
  return images
    .filter((img) => img.s3_key !== placeholderKey)
    .map((img) =>
      img.s3_key === uploaded.s3_key
        ? { ...img, ...localOnly, ...keepFinalVerdict(img, uploaded) }
        : img,
    );
};
